import Router from 'next/router';
import { useContext, useEffect } from 'react';
import SortOption from '~/constants/common/SortOption';
import AppliedFiltersContext from '~/context/common/AppliedFiltersContext';
import RouteContext from '~/context/srp/RouteContext';
import { stripEquals } from '~/utilities/base64';
import decodeCvnaId from '~/utilities/cvnaId';

export default function useNavigation() {
  const { setAppliedFilters, setPageNumber, setSortBy } = useContext(
    AppliedFiltersContext
  );
  const { setCurrentUrl } = useContext(RouteContext);

  // Listen for changes to history and update state accordingly
  useEffect(() => {
    function handleNavigation(_: PopStateEvent) {
      const url = new URL(window.location.href);
      const pageNumberFromUrl = url.searchParams.get('page') ?? '1';
      const { filters = {}, sortBy = SortOption.MostPopular } = decodeCvnaId(
        stripEquals(url.searchParams.get('cvnaid') ?? '')
      );

      setPageNumber.call(null, parseInt(pageNumberFromUrl, 10));
      setAppliedFilters.call(null, filters);
      setSortBy.call(null, sortBy);
    }

    window.addEventListener('popstate', handleNavigation);

    return () => {
      window.removeEventListener('popstate', handleNavigation);
    };
  }, [setAppliedFilters, setPageNumber, setSortBy]);

  // Listen for changes to the URL and update state accordingly
  useEffect(() => {
    function onChange(url: string) {
      setCurrentUrl(url.toLowerCase());
    }

    Router.events.on('routeChangeStart', onChange);

    return () => {
      Router.events.off('routeChangeStart', onChange);
    };
  }, [setCurrentUrl]);

  /**
   * Disable Next's pop state handler
   *
   * Next's router deals with shallow/non-shallow routing differently, and when
   * you refresh and then navigate (back/forward), a XHR request is made to
   * retrieve `getServerSideProps`, which resets stores to their initial state.
   * We decidedly do _not_ want that to happen.
   */
  useEffect(() => {
    Router.beforePopState(_ => false);
  }, []);
}
